import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, UserCircleIcon, PhoneIcon, EnvelopeIcon, ClockIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';

const ReturnDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { getReturnById, getCustomers, getSuppliers, getProducts, getSales } = useAppData();
  const [ret, setRet] = useState(null);
  const [customerHistory, setCustomerHistory] = useState([]);

  const customers = getCustomers();
  const suppliers = getSuppliers();
  const products = getProducts();

  useEffect(() => {
    if (location.state && location.state.returnItem) {
      setRet(location.state.returnItem);
      return;
    }
    const r = getReturnById(id);
    if (r) {
      setRet(r);
      return;
    }
    const raw = localStorage.getItem('stockpro_returns') || localStorage.getItem('returns');
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        const found = parsed.find((x) => x.id === id || x.returnNumber === id);
        setRet(found || null);
      } catch {
        setRet(null);
      }
    }
  }, [id, getReturnById, location.state]);

  const entity = useMemo(() => {
    if (!ret) return null;
    const list = ret.type === 'supplier' ? suppliers : customers;
    return list.find((e) => String(e.id) === String(ret.entityId)) || null;
  }, [ret, customers, suppliers]);
  
  const entityName = useMemo(() => {
    if (!entity) return ret?.entityName || String(ret?.entityId || '');
    const full = `${entity.firstName || ''} ${entity.lastName || ''}`.trim();
    return full || entity.name || entity.company || String(entity.id);
  }, [entity, ret]);
  
  // Charger l'historique des transactions du client
  useEffect(() => {
    if (!ret || ret.type !== 'customer' || !ret.entityId) return;
    
    try {
      // Dans une application réelle, vous feriez un appel API ici
      // Pour cette démo, nous simulons en utilisant les données locales
      const allSales = localStorage.getItem('stockpro_sales');
      if (allSales) {
        const parsedSales = JSON.parse(allSales);
        const customerSales = parsedSales
          .filter(sale => sale.customerId === ret.entityId)
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 3); // Prendre les 3 dernières transactions
        
        setCustomerHistory(customerSales);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique client:', error);
    }
  }, [ret]);

  const resolvedItems = useMemo(() => {
    if (!ret || !Array.isArray(ret.items)) return [];
    return ret.items.map((item) => {
      const prod = products.find((p) => p.id === item.id || p.sku === item.sku);
      const price = Number(item.price ?? prod?.price ?? 0);
      const quantity = Number(item.quantity ?? 0);
      return {
        ...item,
        name: item.name || prod?.name || item.id,
        sku: item.sku || prod?.sku || item.id,
        price,
        quantity,
        total: Number(item.total ?? (price * quantity)),
      };
    });
  }, [ret, products]);

  const formatCurrency = (value) => `${Number(value || 0).toLocaleString()} DA`;

  const totals = useMemo(() => {
    const subtotal = resolvedItems.reduce((s, it) => s + Number(it.total || 0), 0);
    return { subtotal, total: subtotal };
  }, [resolvedItems]);

  if (!ret) {
    return (
      <div className="p-6 bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20">
        <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center text-white/80 hover:text-white">
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Retour
        </button>
        <p className="text-white/60">Retour introuvable.</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20">
      <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center text-white/80 hover:text-white">
        <ArrowLeftIcon className="h-5 w-5 mr-2" />
        Retour
      </button>

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">Retour {ret.returnNumber}</h2>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
          ret.status === 'completed' ? 'bg-green-500/20 text-green-400' : ret.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'
        }`}>
          {ret.status === 'completed' ? 'Accepté' : ret.status === 'pending' ? 'En attente' : 'Refusé'}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-white/60 text-sm">Date</p>
          <p className="text-white font-medium">{ret.date}</p>
        </div>
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-white/60 text-sm">Type</p>
          <p className="text-white font-medium">{ret.type === 'supplier' ? 'Fournisseur' : 'Client'}</p>
        </div>
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-white/60 text-sm">{ret.type === 'supplier' ? 'Fournisseur' : 'Client'}</p>
          <p className="text-white font-medium">{entityName}</p>
        </div>
      </div>

      {/* Additional return details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-white/60 text-sm">Statut</p>
          <div className="mt-1">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${ret.status === 'completed' ? 'bg-green-500/20 text-green-400' : ret.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>
              {ret.status === 'completed' ? 'Accepté' : ret.status === 'pending' ? 'En attente' : 'Refusé'}
            </span>
          </div>
        </div>
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-white/60 text-sm">Raison du retour</p>
          <p className="text-white font-medium">
            {ret.reason === 'defective' ? 'Défectueux' : 
             ret.reason === 'wrong_size' ? 'Mauvaise taille' : 
             ret.reason === 'wrong_item' ? 'Erreur produit' :
             ret.reason === 'damaged' ? 'Endommagé' :
             ret.reason === 'expired' ? 'Expiré' : 
             ret.reason === 'customer_choice' ? 'Choix du client' : ret.reason}
          </p>
        </div>
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-white/60 text-sm">Méthode de remboursement</p>
          <p className="text-white font-medium">
            {ret.refundMethod === 'credit' ? 'Avoir client' : 
             ret.refundMethod === 'exchange' ? 'Échange' : 
             ret.refundMethod === 'refund' ? 'Remboursement' : ret.refundMethod}
          </p>
        </div>
      </div>
      
      {/* Client Information Section - Basic info only */}
      {ret.type === 'customer' && ret.entityEmail && ret.entityPhone && (
        <div className="bg-white/5 rounded-xl p-4 mb-6 border border-white/10">
          <h3 className="text-lg font-medium text-white mb-4">Informations client</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ret.entityEmail && (
              <div className="flex items-center">
                <EnvelopeIcon className="h-5 w-5 text-white/70 mr-2" />
                <span className="text-white">{ret.entityEmail}</span>
              </div>
            )}
            
            {ret.entityPhone && (
              <div className="flex items-center">
                <PhoneIcon className="h-5 w-5 text-white/70 mr-2" />
                <span className="text-white">{ret.entityPhone}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="overflow-x-auto mb-6">
        <table className="w-full">
          <thead className="bg-white/5">
            <tr>
              <th className="px-6 py-3 text-left text-white">Article</th>
              <th className="px-6 py-3 text-left text-white">SKU</th>
              <th className="px-6 py-3 text-right text-white">Prix</th>
              <th className="px-6 py-3 text-right text-white">Qté</th>
              <th className="px-6 py-3 text-right text-white">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {resolvedItems.map((it, idx) => (
              <tr key={idx}>
                <td className="px-6 py-3 text-white">{it.name}</td>
                <td className="px-6 py-3 text-white/70">{it.sku}</td>
                <td className="px-6 py-3 text-right text-white">{formatCurrency(it.price)}</td>
                <td className="px-6 py-3 text-right text-white">{it.quantity}</td>
                <td className="px-6 py-3 text-right text-white">{formatCurrency(it.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <div className="bg-white/5 rounded-xl p-4 min-w-[240px]">
          <div className="flex justify-between text-white/80">
            <span>Sous-total</span>
            <span className="text-white">{formatCurrency(totals.subtotal)}</span>
          </div>
          <div className="mt-2 border-t border-white/10 pt-2 flex justify-between text-white">
            <span className="font-semibold">Total</span>
            <span className="font-semibold">{formatCurrency(totals.total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnDetail;

